import { useEffect, useState } from "react";
import type { SlideProps } from "@/engine/app/offsite-config";
import { api } from "@/api/client";
import { useStore } from "@/state/store";

interface BauStand {
  status: "idle" | "running" | "complete" | "error";
  done: number;
  total: number;
  teams?: number[];
  fertig?: number[];
  fortschritt?: Record<number, number>;
}

/**
 * Live-Lauf-Folie mit echtem Baufortschritt: pro Team ein Kästchen,
 * fertige leuchten blau, laufende zeigen die bereits geschriebenen
 * Zeichen (die Generierungs-Lambda meldet sie während des Streams).
 */
export function Slide13LiveLauf(_: SlideProps) {
  const { state } = useStore();
  const [stand, setStand] = useState<BauStand | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const s = await api.overnight.status(state.auth.token);
        if (!cancelled) setStand(s);
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [state.auth.token]);

  const laufend = stand?.status === "running";
  const komplett = stand?.status === "complete";
  const teams = stand?.teams ?? [];
  const fertigSet = new Set(stand?.fertig ?? []);

  return (
    <div className="deck-root dark" style={{ background: "var(--svo-dunkelblau)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <div
        className="float"
        aria-hidden="true"
        style={{
          width: 460,
          height: 460,
          top: "-120px",
          left: "20%",
          background: "var(--svo-blau)",
          opacity: 0.32,
        }}
      />

      <header className="deck-head" style={{ textAlign: "center", margin: "0 auto" }}>
        <div className="deck-eyebrow rise rise-1">Live-Lauf</div>
      </header>

      <div
        className="deck-lead"
        style={{ margin: "0 auto", textAlign: "center", justifyItems: "center" }}
      >
        <div className="lead-statement rise rise-2">
          {komplett
            ? "Alle Prototypen sind fertig."
            : "Eure Anforderungen gehen jetzt durch die KI."}
        </div>
        <div className="lead-pill rise rise-3" style={{ alignSelf: "center" }}>
          {komplett
            ? "Die KI hat geliefert — gleich seht ihr alles."
            : "Die KI baut — während wir Mittag essen."}
        </div>

        {teams.length > 0 && (
          <div
            className="rise rise-4"
            style={{
              display: "flex",
              gap: 14,
              justifyContent: "center",
              flexWrap: "wrap",
              marginTop: "1.5rem",
            }}
          >
            {teams.map((n) => {
              const fertig = fertigSet.has(n);
              const zeichen = stand?.fortschritt?.[n];
              return (
                <div
                  key={n}
                  style={{
                    minWidth: 120,
                    padding: "14px 18px",
                    borderRadius: "var(--radius-xl)",
                    border: `1.5px solid ${fertig ? "var(--svo-blau)" : "rgba(255,255,255,0.25)"}`,
                    background: fertig ? "rgba(0,141,247,0.25)" : "rgba(255,255,255,0.05)",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: 13, letterSpacing: "0.12em", color: "rgba(255,255,255,0.6)" }}>
                    TEAM {n}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: "clamp(1rem, 1.6vw, 1.3rem)",
                      fontWeight: 600,
                      color: fertig ? "var(--svo-blau)" : "rgba(255,255,255,0.85)",
                    }}
                  >
                    {fertig
                      ? "✓ fertig"
                      : zeichen
                        ? `${(zeichen / 1000).toFixed(1)}k Zeichen …`
                        : laufend
                          ? "baut …"
                          : "wartet"}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="lead-body rise rise-5" style={{ textAlign: "center" }}>
          {komplett ? (
            <>Weiter zum Reveal — schauen wir, was entstanden ist.</>
          ) : laufend ? (
            <>
              <strong>{stand?.done ?? 0}</strong> von <strong>{stand?.total ?? "?"}</strong>{" "}
              Prototypen fertig — direkt nach dem Essen seht ihr alles auf der Leinwand.
            </>
          ) : (
            <>Direkt nach dem Essen seht ihr die fertigen Prototypen auf der Leinwand.</>
          )}
        </div>
      </div>
    </div>
  );
}
